import { useQueryClient } from "@tanstack/react-query";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { domainKeys } from "~/components/siteSpeed/queryKeys";
import { useVisibilityEngineRoutes } from "~/components/siteSpeed/useVisibilityEngineRoutes";

import {
  Domain,
  DomainEdgeIntegrationSteps,
  DomainFullIntegrationSteps,
  DomainInitialSteps,
  DomainPartialIntegrationSteps,
  DomainPluginIntegrationSteps,
} from "../api/Api";

type StepErrors = Partial<Record<WizardStep, boolean>>;

export type WizardStep =
  | DomainInitialSteps[number]
  | DomainFullIntegrationSteps[number]
  | DomainPartialIntegrationSteps[number]
  | DomainEdgeIntegrationSteps[number]
  | DomainPluginIntegrationSteps[number];

interface WizardContextType {
  domain?: Domain;
  currentStep: WizardStep | null;
  incompleteStep: WizardStep | null;
  allSteps: WizardStep[];
  stepErrors: StepErrors;

  canAccessStep: (step: WizardStep) => boolean;

  getNextStep: (step?: WizardStep | null) => WizardStep | null;
  getPrevStep: (step?: WizardStep | null) => WizardStep | null;

  navigateToStep: (step: WizardStep) => void;
  navigateToNextStep: () => void;
  navigateToPrevStep: () => void;

  updateDomain: (
    domain: Domain,
    options?: { navigateToNextStep?: boolean },
  ) => void;

  setStepError: (stepId: WizardStep, hasError: boolean) => void;
  getStepError: (stepId: WizardStep) => boolean;
  getStepTitle: (stepId: WizardStep, defaultTitle: string) => string;
}

const WizardContext = createContext<WizardContextType | undefined>(undefined);

interface WizardProviderProps {
  children: React.ReactNode;
  domain?: Domain;
}

export function parseStepFromPath(
  pathname: string,
  steps: readonly WizardStep[],
): WizardStep | null {
  const step = pathname.match(/\/([^/]+)\/?$/)?.[1];

  return step && steps.includes(step as WizardStep)
    ? (step as WizardStep)
    : null;
}

export const WizardProvider: React.FC<WizardProviderProps> = ({
  children,
  domain,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { domainId } = useParams<{ domainId: string }>();
  const queryClient = useQueryClient();
  const veRoutes = useVisibilityEngineRoutes();

  if (!domainId) {
    throw new Error("No DomainId");
  }

  // Used to defer navigation until after the domain update
  // has been committed and the WizardProvider has re-rendered.
  // This prevents stale state issues when navigating immediately
  // after updating react-query cache.
  const [pendingNavigation, setPendingNavigation] = useState<"next" | null>(
    null,
  );

  const [stepErrors, setStepErrors] = useState<StepErrors>({});

  const queryKey = domainKeys.detail(domainId);

  const incompleteStep = domain?.steps_incomplete?.[0] || null;

  const allSteps = useMemo<WizardStep[]>(
    () => domain?.wizard_steps || [],
    [domain],
  );

  const currentStep = useMemo(
    () => parseStepFromPath(location.pathname, allSteps),
    [location.pathname, allSteps],
  );

  const canAccessStep = useCallback(
    (step: WizardStep): boolean => {
      if (!incompleteStep || !allSteps.length) return true;
      if (!allSteps.includes(step)) return false;

      return allSteps.indexOf(step) <= allSteps.indexOf(incompleteStep);
    },
    [allSteps, incompleteStep],
  );

  const navigateToStep = useCallback(
    (step: WizardStep) => {
      // always invalidate sites
      queryClient.invalidateQueries({ queryKey: domainKeys.sites(domainId) });

      navigate(veRoutes.wizardStep(domainId, step));
    },
    [domainId, navigate, queryClient, veRoutes],
  );

  const getNextStep = useCallback(
    (step?: WizardStep | null): WizardStep | null => {
      const base = step ?? currentStep;
      if (!base || !allSteps.length) return null;

      const idx = allSteps.indexOf(base);
      if (idx === -1 || idx >= allSteps.length - 1) return null;

      const next = allSteps[idx + 1];

      return canAccessStep(next) ? next : null;
    },
    [allSteps, currentStep, canAccessStep],
  );

  const getPrevStep = useCallback(
    (step?: WizardStep | null): WizardStep | null => {
      const base = step ?? currentStep;
      if (!base || !allSteps.length) return null;

      const idx = allSteps.indexOf(base);
      if (idx <= 0) return null;

      return allSteps[idx - 1];
    },
    [allSteps, currentStep],
  );

  const navigateToNextStep = useCallback(() => {
    const next = getNextStep();
    if (next) navigateToStep(next);
  }, [getNextStep, navigateToStep]);

  const navigateToPrevStep = useCallback(() => {
    const prev = getPrevStep();
    if (prev) navigateToStep(prev);
  }, [getPrevStep, navigateToStep]);

  const updateDomain = useCallback(
    (newDomain: Domain, options?: { navigateToNextStep?: boolean }) => {
      queryClient.setQueryData(queryKey, (prev?: Domain) => ({
        ...(prev ?? {}),
        ...newDomain,
      }));

      if (options?.navigateToNextStep) {
        setPendingNavigation("next");
      }
    },
    [queryKey, queryClient],
  );

  const setStepError = useCallback((stepId: WizardStep, hasError: boolean) => {
    setStepErrors((prev) =>
      // Only update if the error state actually changed
      prev[stepId] === hasError ? prev : { ...prev, [stepId]: hasError },
    );
  }, []);

  const getStepError = useCallback(
    (stepId: WizardStep): boolean => stepErrors[stepId] ?? false,
    [stepErrors],
  );

  const getStepTitle = useCallback(
    (stepId: WizardStep, defaultTitle: string): string => {
      if (!stepErrors[stepId]) return defaultTitle;

      if (stepId) {
        switch (stepId) {
          case "dns":
            return "DNS!";
          case "verification":
            return "Verification!";
          case "certificate":
            return "Certificate!";
          case "routing":
            return "Routing!";
          case "ns":
            return "Nameservers!";
          case "confirmation":
            return "Edge Instructions!";
          case "install":
            return "Install!";
          case "configure":
            return "Configure!";
          default:
            return `${defaultTitle}!`;
        }
      }

      return defaultTitle;
    },
    [stepErrors],
  );

  useEffect(() => {
    if (!incompleteStep) return;

    const isWizardPath = location.pathname.includes("/wizard/");
    const rawStep = location.pathname.match(/\/wizard\/([^/]+)/)?.[1];

    /**
     * Case 1:
     * User is on a domain page (not in /wizard),
     * but the domain has incomplete wizard steps.
     * → Redirect to the first incomplete wizard step.
     */
    if (!isWizardPath) {
      navigate(veRoutes.wizardStep(domainId, incompleteStep), {
        replace: true,
      });

      return;
    }

    /**
     * Case 2:
     * User is inside /wizard, but the step from URL
     * is NOT included in server-provided wizard_steps
     * (e.g. direct URL access, outdated bookmark, or integration type not chosen yet).
     * → Redirect to the first incomplete wizard step.
     */
    if (rawStep && !allSteps.includes(rawStep as WizardStep)) {
      navigate(veRoutes.wizardStep(domainId, incompleteStep), {
        replace: true,
      });

      return;
    }

    /**
     * Case 3:
     * User is inside /wizard and the step exists in wizard_steps,
     * but this step is not accessible yet (locked by progression rules).
     * → Redirect to the first incomplete wizard step.
     */
    if (
      rawStep &&
      allSteps.includes(rawStep as WizardStep) &&
      !canAccessStep(rawStep as WizardStep)
    ) {
      navigate(veRoutes.wizardStep(domainId, incompleteStep), {
        replace: true,
      });
    }
  }, [
    domain,
    incompleteStep,
    location.pathname,
    allSteps,
    canAccessStep,
    navigate,
    domainId,
    veRoutes,
  ]);

  // Defer navigation to the next render cycle to avoid stale wizard state.
  useEffect(() => {
    if (!pendingNavigation) return;

    if (pendingNavigation === "next") {
      const next = getNextStep();
      if (next) {
        navigateToStep(next);
        setPendingNavigation(null);
      }
    }
  }, [pendingNavigation, getNextStep, navigateToStep]);

  const contextValue: WizardContextType = {
    domain,
    currentStep,
    incompleteStep,
    allSteps,
    stepErrors,

    canAccessStep,

    getNextStep,
    getPrevStep,

    navigateToStep,
    navigateToNextStep,
    navigateToPrevStep,

    updateDomain,

    setStepError,
    getStepError,
    getStepTitle,
  };

  return (
    <WizardContext.Provider value={contextValue}>
      {children}
    </WizardContext.Provider>
  );
};

export const useWizard = (): WizardContextType => {
  const ctx = useContext(WizardContext);
  if (!ctx) {
    throw new Error("useWizard must be used within a WizardProvider");
  }

  return ctx;
};
